import React, { useEffect, useState } from "react";
import "../styles/PushNotification.css";
import axiosInstance from "../api/axiosInterceptor";
import SyncLoader from "../components/Loader";

const frequencyOptions = ["daily", "weekly", "monthly", "custom_date"];
const backendFrequencyOptions = ["once", "daily", "weekly", "monthly"];
const customEventRegex = /^custom_?(\d+)$/i;

const toUiFrequency = (frequency, date) => {
  if (frequency === "once") return "custom_date";
  if (backendFrequencyOptions.includes(frequency)) return frequency;
  return date ? "custom_date" : "daily";
};

const toBackendFrequency = (uiFrequency) =>
  uiFrequency === "custom_date" ? "once" : uiFrequency;

const parseTimeParts = (timeValue) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeValue || "");
  if (!match) {
    return { hour12: "", minute: "", period: "AM" };
  }

  const hour24 = Number(match[1]);
  const minute = match[2];
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12Num = hour24 % 12 || 12;

  return {
    hour12: String(hour12Num).padStart(2, "0"),
    minute,
    period,
  };
};

const composeTime = (hour12, minute, period) => {
  if (!hour12 || !minute || !period) return "";

  const hour = Number(hour12);
  if (!hour || hour < 1 || hour > 12) return "";

  let hour24 = hour % 12;
  if (period === "PM") hour24 += 12;

  return `${String(hour24).padStart(2, "0")}:${minute}`;
};

const formatTimeDisplay = (timeValue) => {
  const parts = parseTimeParts(timeValue);
  if (!parts.hour12 || !parts.minute) return "-";
  return `${parts.hour12}:${parts.minute} ${parts.period}`;
};

const TimePicker = ({ value, onChange, idPrefix }) => {
  const [parts, setParts] = useState(() => parseTimeParts(value));

  useEffect(() => {
    setParts(parseTimeParts(value));
  }, [value]);

  const hourOptions = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );

  const updatePart = (key, nextValue) => {
    const next = { ...parts, [key]: nextValue };
    setParts(next);
    onChange(composeTime(next.hour12, next.minute, next.period));
  };

  return (
    <div className="time-picker-group">
      <select
        id={`${idPrefix}-hour`}
        value={parts.hour12}
        onChange={(e) => updatePart("hour12", e.target.value)}
      >
        <option value="">Hour</option>
        {hourOptions.map((hr) => (
          <option key={hr} value={hr}>
            {hr}
          </option>
        ))}
      </select>

      <input
        id={`${idPrefix}-minute`}
        type="number"
        min="0"
        max="59"
        placeholder="Min"
        value={parts.minute}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            updatePart("minute", "");
            return;
          }
          const clamped = Math.max(0, Math.min(59, Number(raw)));
          updatePart("minute", String(clamped).padStart(2, "0"));
        }}
      />

      <select
        id={`${idPrefix}-period`}
        value={parts.period}
        onChange={(e) => updatePart("period", e.target.value)}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const parseMessage = (message) => {
  try {
    const parsed = JSON.parse(message);
    if (parsed && typeof parsed === "object") {
      return {
        body: parsed.body || "",
        date: parsed.date || "",
        time: parsed.time || "",
        frequency: toUiFrequency(parsed.frequency, parsed.date),
      };
    }
  } catch (error) {
    // Backward compatibility: if parsing fails, treat message as plain text body.
  }

  return {
    body: message || "",
    date: "",
    time: "",
    frequency: "daily",
  };
};

const buildMessage = ({ body, date, time, frequency }) =>
  JSON.stringify({
    body: body || "",
    date: frequency === "custom_date" ? date || "" : "",
    time: time || "",
    frequency: toBackendFrequency(frequency),
  });

const getNotificationRowKey = (note, index) =>
  note.id || note.pk || `${note.event}-${index}`;

const extractServerErrorMessage = (error, fallback = "Failed to add notification.") => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(" ");
  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (!entries.length) return fallback;
    const [field, value] = entries[0];
    if (Array.isArray(value)) return `${field}: ${value.join(" ")}`;
    if (typeof value === "string") return `${field}: ${value}`;
  }
  return fallback;
};

const isDuplicateTypeError = (error) => {
  const text = extractServerErrorMessage(error, "").toLowerCase();
  return text.includes("already exists") || text.includes("unique");
};

const getCustomEventNumber = (eventCode) => {
  const match = customEventRegex.exec(eventCode || "");
  return match ? Number(match[1]) : null;
};

const getNextCustomEvent = (items) => {
  const maxCustom = (items || []).reduce((maxValue, item) => {
    const current = getCustomEventNumber(item?.event);
    return current && current > maxValue ? current : maxValue;
  }, 0);
  return `custom${maxCustom + 1}`;
};

const PushNotification = () => {
  const baseEvents = ["payment", "payment_success", "live_class_schedule"];
  const eventLabelMap = {
    payment: "System Update",
    payment_success: "Status Update",
    live_class_schedule: "Schedule Update",
  };
  const getEventLabel = (event) => {
    const customNumber = getCustomEventNumber(event);
    if (customNumber) return `Custom ${customNumber}`;
    return eventLabelMap[event] || event;
  };

  const [notifications, setNotifications] = useState([]);
  const nextCustomEvent = getNextCustomEvent(notifications);
  const addEventOptions = [...baseEvents, nextCustomEvent];
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    body: "",
    date: "",
    time: "",
    frequency: "daily",
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    event: "",
    title: "",
    body: "",
    date: "",
    time: "",
    frequency: "daily",
  });
  const [addError, setAddError] = useState("");
  const [showAlert, setShowAlert] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/notifications/");
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (note, rowKey) => {
    const parsed = parseMessage(note.message);
    setEditingRowKey(rowKey);
    setEditForm({
      title: note.title || "",
      body: parsed.body,
      date: parsed.date,
      time: parsed.time,
      frequency: parsed.frequency,
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => {
      if (name === "frequency" && value !== "custom_date") {
        return { ...prev, frequency: value, date: "" };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => {
      if (name === "frequency" && value !== "custom_date") {
        return { ...prev, frequency: value, date: "" };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleAddSubmit = async () => {
    if (!addForm.event) {
      setAddError("Event is required.");
      return;
    }
    const isBaseEvent = baseEvents.includes(addForm.event);
    const isCustomEvent = customEventRegex.test(addForm.event || "");
    if (!isBaseEvent && !isCustomEvent) {
      setAddError("Event must be a predefined type or in customN format (e.g., custom3).");
      return;
    }

    if (addForm.frequency === "custom_date" && !addForm.date) {
      setAddError("Please select a date for Custom date frequency.");
      return;
    }

    setAddError("");
    setIsLoading(true);
    try {
      const payload = {
        event: addForm.event,
        title: addForm.title,
        message: buildMessage(addForm),
      };
      await axiosInstance.post("/admin/notifications/", payload);
      setShowAlert("Notification added successfully!");
      setShowAddForm(false);
      setAddForm({
        event: "",
        title: "",
        body: "",
        date: "",
        time: "",
        frequency: "daily",
      });
      await fetchNotifications();
      setTimeout(() => setShowAlert(""), 3000);
    } catch (error) {
      console.error("Error adding notification:", error);
      if (isDuplicateTypeError(error)) {
        setAddError("This event already exists. Select the next Custom option.");
      } else {
        setAddError(extractServerErrorMessage(error));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (eventCode) => {
    if (editForm.frequency === "custom_date" && !editForm.date) {
      setAddError("Please select a date for Custom date frequency.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        event: eventCode,
        title: editForm.title,
        message: buildMessage(editForm),
      };
      await axiosInstance.put(`/admin/notifications/${eventCode}/`, payload);
      setShowAlert("Notification modified successfully!");
      setEditingRowKey(null);
      await fetchNotifications();
      setTimeout(() => setShowAlert(""), 3000);
    } catch (error) {
      console.error("Error updating notification:", error);
      setAddError(extractServerErrorMessage(error, "Failed to update notification."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (eventCode) => {
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/admin/notifications/${eventCode}/`);
      setShowAlert("Notification deleted successfully!");
      await fetchNotifications();
      setTimeout(() => setShowAlert(""), 3000);
    } catch (error) {
      console.error("Error deleting notification:", error);
      setAddError(extractServerErrorMessage(error, "Failed to delete notification."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <SyncLoader />}
      {showAlert && <div className="success-alert">{showAlert}</div>}

      <div className="push-notification-container">
        <h2>Admin Notification Customizer</h2>

        <div className="add-notification-section">
          <button
            className="btn-pushnotification"
            onClick={() => setShowAddForm((prev) => !prev)}
            disabled={isLoading}
          >
            {showAddForm ? "Cancel" : "Add Notification"}
          </button>

          {showAddForm && (
            <div className="add-form">
              <label>
                <select name="event" value={addForm.event} onChange={handleAddChange}>
                  <option value="">-- select --</option>
                  {addEventOptions.map((ev) => (
                    <option key={ev} value={ev}>
                      {getEventLabel(ev)}
                    </option>
                  ))}
                </select>
              </label>

              <input
                type="text"
                name="title"
                placeholder="Title"
                value={addForm.title}
                onChange={handleAddChange}
              />

              <textarea
                name="body"
                placeholder="Notification body"
                value={addForm.body}
                onChange={handleAddChange}
                rows={3}
              />

              <div className="notification-schedule-grid">
                <select name="frequency" value={addForm.frequency} onChange={handleAddChange}>
                  {frequencyOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === "custom_date" ? "Custom date" : opt}
                    </option>
                  ))}
                </select>
                <TimePicker
                  idPrefix="add-time"
                  value={addForm.time}
                  onChange={(timeValue) =>
                    setAddForm((prev) => ({ ...prev, time: timeValue }))
                  }
                />
                {addForm.frequency === "custom_date" ? (
                  <input type="date" name="date" value={addForm.date} onChange={handleAddChange} />
                ) : null}
              </div>

              {addError && <div className="error-text">{addError}</div>}

              <button
                className="btn-pushnotification"
                onClick={handleAddSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Submit"}
              </button>
            </div>
          )}
        </div>

        <div className="table-wrapper">
          <table className="notification-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Title</th>
                <th>Body</th>
                <th>Frequency</th>
                <th>Time</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((note, index) => {
                const parsed = parseMessage(note.message);
                const rowKey = getNotificationRowKey(note, index);

                return (
                  <tr key={rowKey}>
                    <td>{getEventLabel(note.event)}</td>
                    <td>
                      {editingRowKey === rowKey ? (
                        <input
                          type="text"
                          name="title"
                          value={editForm.title}
                          onChange={handleEditChange}
                        />
                      ) : (
                        note.title
                      )}
                    </td>
                    <td>
                      {editingRowKey === rowKey ? (
                        <textarea
                          name="body"
                          value={editForm.body}
                          onChange={handleEditChange}
                          rows={3}
                        />
                      ) : (
                        <div className="message-cell">{parsed.body}</div>
                      )}
                    </td>
                    <td>
                      {editingRowKey === rowKey ? (
                        <select
                          name="frequency"
                          value={editForm.frequency}
                          onChange={handleEditChange}
                        >
                          {frequencyOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt === "custom_date" ? "Custom date" : opt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        parsed.frequency === "custom_date" ? "Custom date" : parsed.frequency
                      )}
                    </td>
                    <td>
                      {editingRowKey === rowKey ? (
                        <TimePicker
                          idPrefix={`edit-time-${rowKey}`}
                          value={editForm.time}
                          onChange={(timeValue) =>
                            setEditForm((prev) => ({ ...prev, time: timeValue }))
                          }
                        />
                      ) : (
                        formatTimeDisplay(parsed.time)
                      )}
                    </td>
                    <td>
                      {editingRowKey === rowKey ? (
                        editForm.frequency === "custom_date" ? (
                          <input
                            type="date"
                            name="date"
                            value={editForm.date}
                            onChange={handleEditChange}
                          />
                        ) : (
                          <span>-</span>
                        )
                      ) : parsed.frequency === "custom_date" ? (
                        parsed.date || "-"
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {editingRowKey === rowKey ? (
                        <button
                          className="btn-pushnotification"
                          onClick={() => handleSubmit(note.event)}
                          disabled={isLoading}
                        >
                          {isLoading ? "Saving..." : "Save"}
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn-pushnotification"
                            onClick={() => handleEditClick(note, rowKey)}
                            disabled={isLoading}
                          >
                            Change
                          </button>
                          <button
                            className="btn-pushnotification delete"
                            onClick={() => handleDelete(note.event)}
                            disabled={isLoading}
                            style={{ marginLeft: "0.5rem" }}
                          >
                            {isLoading ? "Deleting..." : "Delete"}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default PushNotification;
